import { KompisShareDesignCard } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getVpcCode } from 'ducks/vpc';
import { isKiosk } from 'utils/platform';
import { SendByEmailStateEnum } from '@inter-ikea-kompis/component-send-by-email';
import {
  ShareDesignCardCopyStateEnum,
  ShareDesignCardStateEnum
} from '@inter-ikea-kompis/component-share-design-card';
import { NotificationLinkTypeEnum } from '@inter-ikea-kompis/enums';
import { useAvailability } from 'contexts/availability';
import { getLocale } from 'i18n/i18nUtils';
import Modal, { ModalBody, ModalHeader, Sheets } from '@ingka/modal';
import { hideShareDesignCard, isShareDesignCardOpen } from 'ducks/sheets';
import { useAppDispatch } from 'ducks/hooks';
import { useScreenshot } from 'contexts/screenshot';
import { getDexfApiKey } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getRange } from 'utils/queryUtils';
import { useIntl } from 'react-intl';
import { getDesignLink, getStringMeasurements, wardrobeTextTranslation } from 'views/Summary/utils';
import { getFramesInScene } from 'rangeConfig/shared/utils/colorUtils/utils';
import { NotificationService } from '@inter-ikea-kompis/services';
import INotificationServiceOptions from '@inter-ikea-kompis/services/lib/facades/notification/INotificationServiceOptions';
import withLoading from 'ui/withLoading/withLoading';
import { useTranslations } from 'hooks/useTranslations';
import {
  ReportSourceEnum,
  reportCopyDesignCode,
  reportShareDesignCardStateChange,
  reportShareViaClipboard,
  reportShareViaEmail
} from 'analytics/reporters';
import { useVpc } from 'hooks/useVpc';
import { useServiceSettings } from 'contexts/serviceSettings';

const LoadingModalBody = withLoading(ModalBody);

const ShareDesignCard = () => {
  const coreApi = useCore();
  const range = getRange();
  const { formatMessage } = useIntl();
  const { dexfSettings, translations } = useServiceSettings();
  const { useMetric } = useTranslations();

  const vpcCode = useSelector(getVpcCode);
  const visible = useSelector(isShareDesignCardOpen);
  const dispatch = useAppDispatch();
  const { storeId } = useAvailability();
  const { images } = useScreenshot();
  const locale = getLocale();
  const LINK_TYPE = NotificationLinkTypeEnum.deeplink; // Depends on where ShareDesignCard is used
  const vpc = useVpc();

  const [state, setState] = useState<ShareDesignCardStateEnum>(ShareDesignCardStateEnum.default);
  const [sendByEmailState, setSendByEmailState] = useState(SendByEmailStateEnum.default);
  const [sendByEmailValue, setSendByEmailValue] = useState('');
  const [sendByEmailInvalidInput, setSendByEmailInvalidInput] = useState(false);
  const [copyDesignCodeState, setCopyDesignCodeState] = useState(
    ShareDesignCardCopyStateEnum.default
  );
  const [copyDesignLinkState, setCopyDesignLinkState] = useState(
    ShareDesignCardCopyStateEnum.default
  );
  const productName = range.toUpperCase();
  const dexfApiKey = getDexfApiKey(range);

  const designLink = getDesignLink(storeId, vpcCode);
  const outerMeasurements = coreApi.entities.getOuterMeasurements();
  const productDescription = outerMeasurements
    ? `
    ${formatMessage({ id: wardrobeTextTranslation() })},
    ${getStringMeasurements(outerMeasurements, translations, useMetric)}
    ${formatMessage({ id: 'kompis.measureValue' })}
  `
    : '';
  const frameEntities = getFramesInScene(coreApi);
  const screenshotAlt =
    range.toUpperCase() +
    ' ' +
    formatMessage({ id: 'storageone.storageSolution' }).toLowerCase() +
    ', ' +
    frameEntities.length +
    ' ' +
    formatMessage({ id: 'storageone.productListFramesTitle' }).toLowerCase();

  useEffect(() => {
    // Reset states for options when the state of ShareDesignCard changes so any previous errors/successes are removed when navigating between options.
    setSendByEmailState(SendByEmailStateEnum.default);
    setCopyDesignCodeState(ShareDesignCardCopyStateEnum.default);
    setCopyDesignLinkState(ShareDesignCardCopyStateEnum.default);
    setSendByEmailValue('');
  }, [state]);

  // Trigger if vpc code is null, previously the save function was run multiple times even though we already had a vpc
  useEffect(() => {
    if (!vpcCode && visible) {
      vpc.save({ userTriggered: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, vpcCode]);

  const onSendByEmailSend = ({ detail }: { detail: { value: string } }): void => {
    reportShareViaEmail();
    setSendByEmailState(SendByEmailStateEnum.loading);

    if (dexfSettings) {
      const notificationServiceOptions: INotificationServiceOptions = {
        settings: dexfSettings,
        dexfApiKey
      };

      if (vpcCode) {
        const notificationService = new NotificationService(notificationServiceOptions);
        notificationService
          .sendEmail({ vpcCode, email: detail.value, linkType: LINK_TYPE })
          .then(() => {
            setSendByEmailState(SendByEmailStateEnum.success);
          })
          .catch(() => {
            setSendByEmailState(SendByEmailStateEnum.failed);
          });
      }
    }
  };

  const onClose = () => {
    setState(ShareDesignCardStateEnum.default);
    dispatch(hideShareDesignCard());
  };

  const backBtnClick = () => {
    setState(ShareDesignCardStateEnum.default);
  };

  return (
    <Modal
      data-testid="share-design-card"
      visible={!!visible}
      handleCloseBtn={onClose}
      style={{ zIndex: 500 }}
    >
      <Sheets
        header={
          <ModalHeader
            backBtnClick={state === ShareDesignCardStateEnum.default ? undefined : backBtnClick}
            data-testid="modal-header"
          />
        }
        footer={<></>}
      >
        <LoadingModalBody isLoading={!vpcCode}>
          {translations && dexfSettings && vpcCode && (
            <KompisShareDesignCard
              theme={SkapaTheme}
              settings={dexfSettings}
              translations={translations.kompis}
              locale={locale}
              autocomplete={!isKiosk()} // Disable autocomplete for kiosk
              disableLinks={isKiosk()} // Disable links for kiosk
              state={state}
              onSelect={({ detail }) => {
                reportShareDesignCardStateChange({
                  state: detail.selectedState,
                  source: ReportSourceEnum.shareDesignSheet
                }),
                  setState(detail.selectedState);
              }}
              // Solution specific
              vpcCode={vpcCode}
              designLink={designLink}
              // Email
              showSendByEmail
              sendByEmailState={sendByEmailState}
              sendByEmailValue={sendByEmailValue}
              sendByEmailInvalidInput={sendByEmailInvalidInput}
              onSendByEmailInput={({ detail }) => setSendByEmailValue(detail.value)}
              onSendByEmailValidation={({ detail }) =>
                setSendByEmailInvalidInput(detail.invalidInput)
              }
              onSendByEmailSend={onSendByEmailSend}
              onSendByEmailReset={() => setSendByEmailState(SendByEmailStateEnum.default)}
              // Design Code
              showCopyDesignCodeButton={!isKiosk()} // Disable for kiosk
              copyDesignCodeState={copyDesignCodeState}
              onCopyDesignCodeClick={({ detail }) => {
                reportCopyDesignCode(), setCopyDesignCodeState(detail.state);
              }}
              onCopyDesignCodeReset={() =>
                setCopyDesignCodeState(ShareDesignCardCopyStateEnum.default)
              }
              // Design Link
              showGetLink
              copyDesignLinkState={copyDesignLinkState}
              onCopyDesignLinkClick={({ detail }) => {
                reportShareViaClipboard(), setCopyDesignLinkState(detail.state);
              }}
              onCopyDesignLinkReset={() =>
                setCopyDesignLinkState(ShareDesignCardCopyStateEnum.default)
              }
              // Product description
              screenshotUrl={images && images[0]}
              screenshotAlt={screenshotAlt}
              productName={productName}
              productDescription={productDescription}
              // QR Code
              showQrCode // Is false by default
            />
          )}
        </LoadingModalBody>
      </Sheets>
    </Modal>
  );
};

export default ShareDesignCard;
